import { Controller } from "@hotwired/stimulus"
import { applyCartesian } from "@poetry/charts/live/renderer"
import { captureGeometry, matchJobs, applyJobs, finishJobs } from "@poetry/charts/motion/flip"
import { tween } from "@poetry/charts/motion/tween"

/**
 * The live channel: a chart rendered with live: true carries
 * its {spec, frame} payload in the DOM; this controller re-renders
 * client-side (the kernel renderer, in-place attribute updates) whenever
 * new data arrives - no server round trip. Two channels feed one path:
 *
 *   1. Replace the payload script's JSON - exactly what a
 *      turbo_stream.replace/update does. A MutationObserver picks it up.
 *   2. The JS/event API: dispatch `poetry-chart:update` with
 *      { detail: { data } } on the frame, or call update(data) on the
 *      controller instance.
 *
 * Updates animate through the shared FLIP machinery (old geometry ->
 * new, the same cross-render morph); a structure change (window growth, series
 * change) snaps instead of morphing. The tooltip's
 * embedded coordinates are regenerated by the renderer and a
 * live:updated dispatch tells the tooltip controller to re-read them,
 * so it serves fresh values mid-stream. prefers-reduced-motion snaps
 * every update.
 */
export default class ChartLiveController extends Controller {
  // The events this controller dispatches (manifest surface;
  // events_declaration.test.js enforces the list stays honest).
  static events = ["poetry--charts--live:settled", "poetry--charts--live:updated"]

  static targets = ["payload"]

  /**
   * Finds the SVG and starts the payload observer (the
   * script-replacement channel).
   */
  connect() {
    this.svg = this.element.querySelector('[data-slot="chart-svg"]')
    if (!this.svg || !this.hasPayloadTarget) return

    this.observer = new MutationObserver((records) => this.#payloadChanged(records))
    this.observer.observe(this.element, { subtree: true, childList: true, characterData: true })
  }

  /** Stops the observer and abandons any in-flight tween. */
  disconnect() {
    this.observer?.disconnect()
    this.observer = null
    this.cancel?.()
    this.cancel = null
  }

  /**
   * The event channel's action: a poetry-chart:update event's
   * detail.data re-renders.
   *
   * @param {CustomEvent} event
   */
  receive(event) {
    this.update(event.detail.data)
  }

  /**
   * Applies a window: [start, end] inclusive indices into the FULL
   * data; the renderer slices before computing. Instant renders - a
   * drag should track the pointer, not tween behind it.
   *
   * @param {[number, number]} window
   */
  setWindow(window) {
    const payload = this.#payload()
    payload.frame.window = window
    this.writing = true
    try {
      this.payloadTarget.textContent = JSON.stringify(payload)
    } finally {
      queueMicrotask(() => { this.writing = false })
    }
    this.#render(payload, { animate: false })
  }

  /**
   * The interactive legend's click action (the series key rides an
   * action param): hidden series leave the domain (the chart rescales
   * on hide), their marks hide, their legend item dims.
   *
   * @param {Event} event - params.key names the series
   */
  toggleSeries(event) {
    const key = event.params.key
    if (!key) return
    this.hiddenKeys ??= new Set()
    if (this.hiddenKeys.has(key)) this.hiddenKeys.delete(key)
    else this.hiddenKeys.add(key)

    for (const item of this.element.querySelectorAll('[data-slot="chart-legend-item"][data-key]')) {
      item.toggleAttribute("data-hidden", this.hiddenKeys.has(item.dataset.key))
    }

    const payload = this.#payload()
    payload.frame.hidden = [...this.hiddenKeys]
    this.writing = true
    try {
      this.payloadTarget.textContent = JSON.stringify(payload)
    } finally {
      queueMicrotask(() => { this.writing = false })
    }
    this.#render(payload)
  }

  /**
   * The JS API: hosts hand over the new data rows; the payload script
   * is rewritten first (the DOM always holds current state), then
   * rendered.
   *
   * @param {Array<Object>} data - the full replacement rows
   */
  update(data) {
    const payload = this.#payload()
    payload.spec.data = data
    this.writing = true
    try {
      this.payloadTarget.textContent = JSON.stringify(payload)
    } finally {
      queueMicrotask(() => { this.writing = false })
    }
    this.#render(payload)
  }

  // -- internals -------------------------------------------------------------

  // The script-replacement channel: react only to mutations touching the
  // payload script (the renderer's own coordinates rewrite passes by).
  #payloadChanged(records) {
    if (this.writing) return
    const touchesPayload = records.some((record) => {
      const node = record.target.nodeType === Node.TEXT_NODE ? record.target.parentNode : record.target
      if (node?.matches?.('[data-slot="chart-live-payload"]')) return true
      return [...record.addedNodes].some((added) => added.matches?.('[data-slot="chart-live-payload"]'))
    })
    if (touchesPayload) this.#render(this.#payload())
  }

  #payload() {
    return JSON.parse(this.payloadTarget.textContent)
  }

  #render(payload, { animate = true } = {}) {
    this.cancel?.()
    const before = captureGeometry(this.svg)
    applyCartesian(this.element, payload)
    this.dispatch("updated") // the tooltip re-reads the fresh coordinates

    if (!animate || this.#reducedMotion()) {
      this.#settle()
      return
    }
    const jobs = matchJobs(this.svg, before)
    if (!jobs) {
      // Structure changed (window growth, gaps appearing) - snap;
      // added/removed data never morphs.
      this.#settle()
      return
    }

    this.svg.setAttribute("data-motion", "morph")
    applyJobs(jobs, 0) // FLIP: start from the previous geometry
    this.cancel = tween({
      duration: this.#number("--poetry-motion-duration", 1500),
      easing: this.#styleVar("--poetry-motion-easing") ?? "ease",
      onFrame: (eased) => applyJobs(jobs, eased),
      onFinish: () => {
        finishJobs(jobs)
        this.#settle()
      },
    })
  }

  #settle() {
    this.svg.setAttribute("data-motion", "settled")
    this.dispatch("settled")
  }

  #styleVar(name) {
    const match = (this.svg.getAttribute("style") ?? "").match(new RegExp(`${name}:\\s*([^;]+)`))
    return match ? match[1].trim() : null
  }

  #number(name, fallback) {
    const value = parseFloat(this.#styleVar(name))
    return Number.isFinite(value) ? value : fallback
  }

  #reducedMotion() {
    return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
